import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import InteractiveTerminal from "../components/InteractiveTerminal";
import LabWebIDE from "../components/LabWebIDE";
import MarkdownLite from "../components/MarkdownLite";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Terminal,
  Clock,
  CheckCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Trophy,
  Square,
  Lightbulb,
  Sparkles,
  BookOpen,
  HelpCircle,
  Code2,
} from "lucide-react";

const LETTERS = ["A", "B", "C", "D", "E", "F"];

const LabRunner = () => {
  const { labId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [lab, setLab] = useState(null);
  const [session, setSession] = useState(null);
  const [steps, setSteps] = useState([]);
  const [starting, setStarting] = useState(true);
  const [error, setError] = useState(null);

  const [stepIndex, setStepIndex] = useState(0);
  const [completed, setCompleted] = useState([]);
  const [score, setScore] = useState(0);
  const [validating, setValidating] = useState(false);
  const [feedback, setFeedback] = useState(null); // {valid, text}
  const [quizAnswer, setQuizAnswer] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [finished, setFinished] = useState(false);
  const [rightTab, setRightTab] = useState("terminal"); // terminal | ide
  const endedRef = useRef(false);

  // Démarrage : lab + session avec l'environnement CONFIGURÉ (aucun choix étudiant)
  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      try {
        const labRes = await axios.get(`${API}/labs/${labId}`);
        if (cancelled) return;
        setLab(labRes.data);
        setRemaining((labRes.data.estimated_time_minutes || 120) * 60);
        const startRes = await axios.post(
          `${API}/labs/sessions/start`,
          { lab_id: labId, environment: labRes.data.environment },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        setSession(startRes.data);
        setSteps(startRes.data.steps || labRes.data.steps || []);
        if (startRes.data.mode !== "docker" && labRes.data.execution_mode === "docker") {
          toast.warning("Conteneur réel indisponible — démarrage en simulation.");
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "Impossible de démarrer le lab.");
      } finally {
        if (!cancelled) setStarting(false);
      }
    };
    boot();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labId]);

  const endSession = useCallback(() => {
    if (endedRef.current || !session?.session_id) return;
    endedRef.current = true;
    axios.post(`${API}/labs/sessions/${session.session_id}/end`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  }, [session, token]);

  useEffect(() => () => endSession(), [endSession]);

  useEffect(() => {
    if (remaining === null || starting) return;
    const t = setInterval(() => setRemaining((r) => (r > 0 ? r - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [remaining, starting]);

  // Reset l'état d'étape à chaque changement
  useEffect(() => {
    setFeedback(null);
    setQuizAnswer(null);
    setShowHint(false);
  }, [stepIndex]);

  const stopLab = () => { endSession(); navigate(`/labs/${labId}`); };

  const fmtTime = (s) => {
    if (s === null) return "--:--";
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  };

  const step = steps[stepIndex];
  const hasSteps = steps.length > 0;
  const progress = hasSteps ? Math.round((completed.length / steps.length) * 100) : 0;
  const isStepDone = completed.includes(stepIndex);

  const validateStep = async () => {
    if (!session?.session_id || !step) return;
    if (step.step_type === "quiz" && quizAnswer === null) {
      toast.error("Sélectionnez une réponse.");
      return;
    }
    setValidating(true);
    setFeedback(null);
    try {
      const res = await axios.post(
        `${API}/labs/sessions/${session.session_id}/validate-step`,
        { step_index: stepIndex, answer: quizAnswer },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { valid, feedback: fb, score: newScore, completed: allDone } = res.data;
      setFeedback({ valid, text: fb });
      if (valid) {
        setCompleted((c) => (c.includes(stepIndex) ? c : [...c, stepIndex]));
        setScore(newScore);
        if (allDone) {
          setTimeout(() => setFinished(true), 800);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de validation");
    } finally {
      setValidating(false);
    }
  };

  const goPrev = () => setStepIndex((i) => Math.max(0, i - 1));
  const goNext = () => setStepIndex((i) => Math.min(steps.length - 1, i + 1));

  // ---------- Rendu ----------
  if (starting) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center text-center px-4">
        <div className="w-20 h-20 rounded-full border-2 border-primary/30 flex items-center justify-center mb-6">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
        <p className="text-muted-foreground text-sm mb-1">Provisionnement de l'environnement…</p>
        <h2 className="text-2xl font-heading font-bold text-primary">Préparation du lab</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center text-center px-4">
        <p className="text-red-400 mb-4">{error}</p>
        <Button variant="outline" onClick={() => navigate(`/labs/${labId}`)}>Retour au lab</Button>
      </div>
    );
  }

  const modeBadge = session?.mode === "docker" || session?.mode === "ssh" ? "● environnement réel" : session?.mode === "external" ? "service externe" : "simulation";

  return (
    <div className="h-screen flex flex-col bg-[#0a0a0a] text-foreground overflow-hidden">
      {/* Barre supérieure */}
      <header className="flex items-center justify-between px-4 h-12 border-b border-[#222] bg-[#111] shrink-0 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={stopLab} className="text-muted-foreground hover:text-foreground"><ChevronLeft className="w-5 h-5" /></button>
          <Terminal className="w-4 h-4 text-primary shrink-0" />
          <span className="font-medium truncate">{lab?.title}</span>
          <Badge variant="outline" className="text-[10px] uppercase hidden md:inline-flex">{lab?.environment}</Badge>
        </div>

        {/* Barre de progression segmentée */}
        {hasSteps && (
          <div className="hidden md:flex items-center gap-1 flex-1 max-w-md">
            {steps.map((_, i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full ${completed.includes(i) ? "bg-primary" : i === stepIndex ? "bg-primary/50" : "bg-[#333]"}`} />
            ))}
          </div>
        )}

        <div className="flex items-center gap-3">
          <span className="text-xs font-mono px-2 py-0.5 rounded bg-[#1a1a1a] text-muted-foreground hidden sm:inline">{modeBadge}</span>
          {hasSteps && <span className="text-sm font-medium">Score : {score}</span>}
          <span className="flex items-center gap-1.5 text-sm font-mono text-muted-foreground"><Clock className="w-4 h-4" /> {fmtTime(remaining)}</span>
          <Button size="sm" variant="destructive" onClick={stopLab}><Square className="w-3.5 h-3.5 mr-1.5" /> Quitter</Button>
        </div>
      </header>

      {/* Split view */}
      <div className="flex-1 flex min-h-0">
        {/* Panneau gauche */}
        <div className="w-[44%] max-w-2xl border-r border-[#222] flex flex-col min-h-0 bg-[#0d0d0d]">
          {finished ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <Trophy className="w-14 h-14 text-primary mb-4" />
              <h2 className="text-2xl font-heading font-bold mb-2">Lab terminé !</h2>
              <p className="text-muted-foreground mb-1">Score final</p>
              <p className="text-4xl font-bold text-primary mb-6">{score}</p>
              <Button onClick={stopLab}>Terminer</Button>
            </div>
          ) : hasSteps ? (
            <>
              {/* En-tête étape */}
              <div className="flex items-center justify-between px-4 h-11 border-b border-[#222] shrink-0">
                <span className="text-sm font-medium">Étape {stepIndex + 1} / {steps.length}</span>
                {isStepDone && <Badge className="bg-primary/15 text-primary border-primary/30 text-[11px]"><CheckCircle className="w-3 h-3 mr-1" /> Validée</Badge>}
              </div>

              {/* Contenu étape */}
              <div className="flex-1 overflow-y-auto p-5">
                <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wide text-primary/80">
                  {step?.step_type === "quiz" ? <><HelpCircle className="w-4 h-4" /> Question</> : <><BookOpen className="w-4 h-4" /> Théorie & instructions</>}
                </div>
                <h1 className="font-heading font-bold text-xl mb-4">{step?.title}</h1>

                <MarkdownLite content={step?.content || ""} />

                {/* Quiz */}
                {step?.step_type === "quiz" && (
                  <div className="mt-5 space-y-2">
                    {(step.quiz_options || []).map((opt, i) => (
                      <button
                        key={i}
                        onClick={() => setQuizAnswer(i)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-colors ${quizAnswer === i ? "border-primary bg-primary/10" : "border-[#222] hover:border-primary/40"}`}
                      >
                        <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm shrink-0 ${quizAnswer === i ? "bg-primary text-primary-foreground" : "bg-[#1a1a1a] text-muted-foreground"}`}>{LETTERS[i]}</span>
                        <span className="text-sm">{opt}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Indice */}
                {step?.hint && (
                  <div className="mt-4">
                    <button onClick={() => setShowHint((s) => !s)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                      <Lightbulb className="w-3.5 h-3.5" /> {showHint ? "Masquer l'indice" : "Afficher un indice"}
                    </button>
                    {showHint && <p className="text-sm text-yellow-400/90 mt-2 p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/20">{step.hint}</p>}
                  </div>
                )}

                {/* Feedback IA */}
                {feedback && (
                  <div className={`mt-4 p-3 rounded-lg border flex items-start gap-2 text-sm ${feedback.valid ? "border-primary/30 bg-primary/10 text-primary" : "border-red-500/30 bg-red-500/10 text-red-300"}`}>
                    <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{feedback.text || (feedback.valid ? "Validé !" : "Pas encore — réessayez.")}</span>
                  </div>
                )}
              </div>

              {/* Barre d'actions */}
              <div className="flex items-center justify-between gap-2 px-4 h-16 border-t border-[#222] shrink-0">
                <Button variant="outline" size="sm" onClick={goPrev} disabled={stepIndex === 0}>
                  <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
                </Button>
                <div className="flex items-center gap-2">
                  {step?.step_type !== "instruction" && !isStepDone && (
                    <Button size="sm" onClick={validateStep} disabled={validating}>
                      {validating ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> {step?.step_type === "task" ? "Analyse IA…" : "Vérification…"}</> : <><Sparkles className="w-4 h-4 mr-1.5" /> Valider</>}
                    </Button>
                  )}
                  {(isStepDone || step?.step_type === "instruction") && stepIndex < steps.length - 1 && (
                    <Button size="sm" onClick={() => { if (step?.step_type === "instruction" && !isStepDone) validateStep(); goNext(); }}>
                      Suivant <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                  {(isStepDone || step?.step_type === "instruction") && stepIndex === steps.length - 1 && (
                    <Button size="sm" onClick={() => { if (!isStepDone) validateStep(); setFinished(true); }}>
                      <Trophy className="w-4 h-4 mr-1.5" /> Terminer
                    </Button>
                  )}
                </div>
              </div>
            </>
          ) : (
            // Fallback : lab sans steps → instructions simples
            <div className="flex-1 overflow-y-auto p-5">
              <h1 className="font-heading font-bold text-xl mb-2">{lab?.title}</h1>
              <p className="text-muted-foreground mb-4">{lab?.description}</p>
              <MarkdownLite content={lab?.instructions || ""} />
            </div>
          )}
        </div>

        {/* Panneau droit : Terminal | WebIDE */}
        <div className="flex-1 flex flex-col min-h-0 bg-[#0a0a0a]">
          <div className="flex items-center gap-1 px-3 h-11 border-b border-[#222] shrink-0">
            <button
              onClick={() => setRightTab("terminal")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm ${rightTab === "terminal" ? "bg-[#1a1a1a] text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              <Terminal className="w-4 h-4" /> Terminal
            </button>
            <button
              onClick={() => setRightTab("ide")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm ${rightTab === "ide" ? "bg-[#1a1a1a] text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              <Code2 className="w-4 h-4" /> WebIDE
            </button>
          </div>
          {/* Les deux restent montés (état préservé) ; on masque l'inactif */}
          <div className="flex-1 min-h-0 p-3 relative">
            <div className={`absolute inset-3 ${rightTab === "terminal" ? "" : "hidden"}`}>
              {session && (
                <InteractiveTerminal
                  labId={labId}
                  environment={lab?.environment || "docker"}
                  externalSessionId={session.session_id}
                  externalMode={session.mode}
                  fill
                />
              )}
            </div>
            <div className={`absolute inset-3 ${rightTab === "ide" ? "" : "hidden"}`}>
              {session && <LabWebIDE sessionId={session.session_id} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LabRunner;
